export const TRAINING_ACCOUNT_PASSWORD = 'flashflash';
export const TRAINING_ACCOUNT_BASE_EMAIL_DOMAIN = 'luxuryescapes.com';
export const TRAINING_ACCOUNT_SURNAME = 'training';

export type TrainingScenarioKey =
  | 'scenario_1'
  | 'scenario_2'
  | 'scenario_3'
  | 'scenario_4'
  | 'scenario_5'
  | 'scenario_6'
  | 'scenario_7'
  | 'scenario_8'
  | 'scenario_9';

export type TrainingScenarioAncillaries = {
  flight?: {
    originAirportCode: string;
    destinationAirportCode: string;
    fareClass?: string;
    fareType?: 'return';
    source?: string;
    viewType?: string;
  };
  travelInsurance?: {
    destinationCountries: Array<string>;
  };
  complimentaryExperiences?: boolean;
};

export type TrainingScenarioGiftExperience = {
  offerId: string;
  sourceUrl: string;
  title: string;
  ticketId: string;
  productId: string;
  ticketFareType: string;
  ticketType: string;
  price: number;
  currencyCode: string;
  bookingType: 'buy_now_book_later';
  bookByDate: string;
  expirationDate: string;
  locationTimezone: string;
};

export type TrainingScenarioBedbankRateSelection = 'exact' | 'refundable' | 'non_refundable';

export type TrainingScenarioBedbankFallbackBooking = {
  offerId: string;
  label: string;
  packageId?: string;
  roomRateId?: string;
};

export type TrainingScenarioBookingConfig = {
  offerId: string;
  packageId: string;
  roomRateId: string;
  offerType?: string;
  numberOfNights: number;
  checkInMonthsFromNow?: number;
  checkInAdditionalDays?: number;
  resolveHotelPricing?: boolean;
  resolveOutsidePolicyByTrainingDate?: boolean;
  bedbankRateSelection?: TrainingScenarioBedbankRateSelection;
  fallbackBookings?: Array<TrainingScenarioBedbankFallbackBooking>;
  ancillaries?: TrainingScenarioAncillaries;
};

type TrainingAccountScenarioBase = {
  key: TrainingScenarioKey;
  scenarioNumber: number;
  label: string;
  description: string;
};

export type TrainingAccountScenario = TrainingAccountScenarioBase & {
  booking?: TrainingScenarioBookingConfig;
  giftExperience?: TrainingScenarioGiftExperience;
};

export type TrainingAccountRegistrationPayload = {
  givenName: string;
  surname: string;
  email: string;
  password: string;
};

export type TrainingAccountRegistration = {
  baseEmail: string;
  scenarioKey: TrainingScenarioKey;
  scenarioLabel: string;
  email: string;
  payload: TrainingAccountRegistrationPayload;
};

const ANVAYA_OFFER_ID = '0062y00000G3hPjAAJ';
const ANVAYA_DELUXE_SUITE_PACKAGE_ID = 'a0s2y000002XbPSAA0';
const ANVAYA_DELUXE_SUITE_ROOM_RATE_ID = '12b21ad0-2a39-11f0-af20-211bd83652e9';
const ANVAYA_BEACH_FRONT_PRIVATE_SUITE_PACKAGE_ID = 'a0s2y000002XbPcAAK';
const ANVAYA_BEACH_FRONT_PRIVATE_SUITE_ROOM_RATE_ID = 'd6ef7d80-2a38-11f0-b9da-b16d21407ce7';
const BEDBANK_FALLBACK_BOOKINGS: Array<TrainingScenarioBedbankFallbackBooking> = [
  { offerId: '00015856-3c64-4682-9ca3-df2baf0ebaa2', label: 'Best Western Plus Paramount Hotel' },
  { offerId: '000151cf-24ed-439c-97e3-168cabb254bd', label: 'Shore Amora Canggu' },
  { offerId: '0000191e-a6c8-404b-b3e9-7caff15b0f44', label: 'The Pavilions Himalayas Lake View' },
  { offerId: '000c3126-7e84-4838-bf32-1f23ec44fc51', label: 'Eurostars Atlántico' },
  { offerId: '00053778-67d9-427e-b82f-2896a775a67a', label: 'Khanom Beach Resort & Spa' },
  {
    offerId: '00005104-a0b8-4693-ae85-67a7a006ead4',
    label: 'Four Points by Sheraton Manhattan SoHo Village',
  },
];

export const trainingAccountScenarios: Array<TrainingAccountScenario> = [
  {
    key: 'scenario_1',
    scenarioNumber: 1,
    label: 'LTLE upgrade path',
    description: 'Deluxe Suite, six-night stay starting four months from now.',
    booking: {
      offerId: ANVAYA_OFFER_ID,
      packageId: ANVAYA_DELUXE_SUITE_PACKAGE_ID,
      roomRateId: ANVAYA_DELUXE_SUITE_ROOM_RATE_ID,
      numberOfNights: 6,
      checkInMonthsFromNow: 4,
      resolveHotelPricing: true,
    },
  },
  {
    key: 'scenario_2',
    scenarioNumber: 2,
    label: 'Date change & BNBL',
    description: 'Deluxe Suite, six-night stay starting four months from now.',
    booking: {
      offerId: ANVAYA_OFFER_ID,
      packageId: ANVAYA_DELUXE_SUITE_PACKAGE_ID,
      roomRateId: ANVAYA_DELUXE_SUITE_ROOM_RATE_ID,
      numberOfNights: 6,
      checkInMonthsFromNow: 4,
      resolveHotelPricing: true,
    },
  },
  {
    key: 'scenario_3',
    scenarioNumber: 3,
    label: 'LTLE used for downgrade',
    description: 'Beach Front Private Suite, seven-night stay starting four months from now.',
    booking: {
      offerId: ANVAYA_OFFER_ID,
      packageId: ANVAYA_BEACH_FRONT_PRIVATE_SUITE_PACKAGE_ID,
      roomRateId: ANVAYA_BEACH_FRONT_PRIVATE_SUITE_ROOM_RATE_ID,
      numberOfNights: 7,
      checkInMonthsFromNow: 4,
      resolveHotelPricing: true,
    },
  },
  {
    key: 'scenario_4',
    scenarioNumber: 4,
    label: 'LTLE flights insurance experiences',
    description: 'Deluxe Suite, six-night stay with flights, insurance, and experience inclusions.',
    booking: {
      offerId: ANVAYA_OFFER_ID,
      packageId: ANVAYA_DELUXE_SUITE_PACKAGE_ID,
      roomRateId: ANVAYA_DELUXE_SUITE_ROOM_RATE_ID,
      numberOfNights: 6,
      checkInMonthsFromNow: 4,
      resolveHotelPricing: true,
      ancillaries: {
        flight: {
          originAirportCode: 'MELALL',
          destinationAirportCode: 'DPS',
          fareClass: 'economy',
          fareType: 'return',
          source: 'hotel',
          viewType: 'TwoOneWays',
        },
        travelInsurance: {
          destinationCountries: ['Indonesia'],
        },
      },
    },
  },
  {
    key: 'scenario_5',
    scenarioNumber: 5,
    label: 'LPC within policy',
    description: 'LPC accommodation scenario within policy, five-night stay starting four months from now.',
    booking: {
      offerId: '0061s000006pUf5AAE',
      packageId: 'a0s1s000001LvlFAAS',
      roomRateId: '89055730-2b1c-11ec-b721-91c0e33617fb',
      numberOfNights: 5,
      checkInMonthsFromNow: 4,
      resolveHotelPricing: true,
    },
  },
  {
    key: 'scenario_6',
    scenarioNumber: 6,
    label: 'LPP within policy',
    description: 'Hoxton Shoreditch Cosy Double refundable booking, three-night stay starting four months from now.',
    booking: {
      offerId: '00a66146-bdd3-4230-9a27-2dbdf56c2ceb',
      packageId: 'a52dfb9f-441b-4454-ac47-431ec7aa6a4b',
      roomRateId: 'S1|a52dfb9f-441b-4454-ac47-431ec7aa6a4b|RO|1000|NP',
      offerType: 'bedbank_hotel',
      numberOfNights: 3,
      checkInMonthsFromNow: 4,
      resolveHotelPricing: true,
      bedbankRateSelection: 'refundable',
      fallbackBookings: BEDBANK_FALLBACK_BOOKINGS,
    },
  },
  {
    key: 'scenario_7',
    scenarioNumber: 7,
    label: 'LPP outside policy',
    description:
      'Hoxton Shoreditch Cosy Double refundable booking selected to be outside cancellation policy on the training date.',
    booking: {
      offerId: '00a66146-bdd3-4230-9a27-2dbdf56c2ceb',
      packageId: 'a52dfb9f-441b-4454-ac47-431ec7aa6a4b',
      roomRateId: 'S1|a52dfb9f-441b-4454-ac47-431ec7aa6a4b|RO|1000|NP',
      offerType: 'bedbank_hotel',
      numberOfNights: 3,
      resolveHotelPricing: true,
      resolveOutsidePolicyByTrainingDate: true,
      bedbankRateSelection: 'refundable',
      fallbackBookings: BEDBANK_FALLBACK_BOOKINGS,
    },
  },
  {
    key: 'scenario_8',
    scenarioNumber: 8,
    label: 'LPP non refundable',
    description: 'Hoxton Shoreditch Cosy Double non-refundable booking, three-night stay starting three days from now.',
    booking: {
      offerId: '00a66146-bdd3-4230-9a27-2dbdf56c2ceb',
      packageId: 'a52dfb9f-441b-4454-ac47-431ec7aa6a4b',
      roomRateId: 'S1|a52dfb9f-441b-4454-ac47-431ec7aa6a4b|RO|0000|NP',
      offerType: 'bedbank_hotel',
      numberOfNights: 3,
      checkInMonthsFromNow: 0,
      checkInAdditionalDays: 3,
      resolveHotelPricing: true,
      bedbankRateSelection: 'non_refundable',
      fallbackBookings: BEDBANK_FALLBACK_BOOKINGS,
    },
  },
  {
    key: 'scenario_9',
    scenarioNumber: 9,
    label: 'Gifted experience voucher',
    description: 'Standalone gifted Khao Lak Takua Pa private tour voucher sent to a derived gift training recipient.',
    giftExperience: {
      offerId: 'led:0062y00000FioSLAAZ',
      sourceUrl:
        'https://test-customer.luxuryescapes.com/au/experience/khao-lak-immerse-yourself-in-a-cultural-half-day-takua-pa-private-tour-with-return-transfers/led:0062y00000FioSLAAZ',
      title: 'Immerse Yourself in a Cultural Half-Day Takua Pa Private Tour with Return Transfers',
      ticketId: 'a0sRF00000KfcaVYAR',
      productId: 'a0sRF00000KfcaVYAR',
      ticketFareType:
        '(01/11/25-31/12/26) Khao Lak Private Tour - Half Day Takua Pa Experience - (01/11/25-31/12/26) Khao Lak Private Tour - Half Day Takua Pa Experience',
      ticketType: 'led',
      price: 104,
      currencyCode: 'AUD',
      bookingType: 'buy_now_book_later',
      bookByDate: '2026-12-15T16:59:59.000Z',
      expirationDate: '2026-12-31T16:59:59.000Z',
      locationTimezone: 'Asia/Jakarta',
    },
  },
];

const SCENARIO_NUMBER_WORDS: Record<number, string> = {
  1: 'One',
  2: 'Two',
  3: 'Three',
  4: 'Four',
  5: 'Five',
  6: 'Six',
  7: 'Seven',
  8: 'Eight',
  9: 'Nine',
};

function splitTrainingBaseEmail(baseEmail: string): { localPart: string; domain: string } {
  const email = baseEmail.trim();

  if (!email) {
    throw new Error('A new-hire email is required');
  }

  const [localPart, domain, ...extraParts] = email.split('@');
  if (!localPart || !domain || extraParts.length > 0) {
    throw new Error('A valid new-hire email is required');
  }

  const normalizedDomain = domain.toLowerCase();
  if (normalizedDomain !== TRAINING_ACCOUNT_BASE_EMAIL_DOMAIN) {
    throw new Error('A luxuryescapes.com new-hire email is required');
  }

  return { localPart, domain: normalizedDomain };
}

export function deriveTrainingEmail(baseEmail: string, scenarioNumber: number): string {
  const { localPart, domain } = splitTrainingBaseEmail(baseEmail);

  return `${localPart}+training${scenarioNumber}@${domain}`;
}

export function deriveGiftTrainingEmail(baseEmail: string): string {
  const { localPart, domain } = splitTrainingBaseEmail(baseEmail);

  return `${localPart}+gifttraining@${domain}`;
}

function deriveTrainingGivenName(baseEmail: string, scenarioNumber: number): string {
  const [localPart] = baseEmail.split('@');
  const [nameSegment] = localPart.split(/[._+-]/);
  const baseName = `${nameSegment.charAt(0).toUpperCase()}${nameSegment.slice(1).toLowerCase()}`;

  return `${baseName}T${SCENARIO_NUMBER_WORDS[scenarioNumber] ?? scenarioNumber}`;
}

export function buildTrainingAccountRegistrations(
  baseEmail: string,
  scenarioKeys?: Array<TrainingScenarioKey>,
): Array<TrainingAccountRegistration> {
  const selectedScenarioKeys = new Set(scenarioKeys);

  return trainingAccountScenarios
    .filter((scenario) => !scenarioKeys || selectedScenarioKeys.has(scenario.key))
    .map((scenario) => {
      const email = deriveTrainingEmail(baseEmail, scenario.scenarioNumber);
      const givenName = deriveTrainingGivenName(baseEmail, scenario.scenarioNumber);

      return {
        baseEmail,
        scenarioKey: scenario.key,
        scenarioLabel: scenario.label,
        email,
        payload: {
          givenName,
          surname: TRAINING_ACCOUNT_SURNAME,
          email,
          password: TRAINING_ACCOUNT_PASSWORD,
        },
      };
    });
}
